/**
 * `COMPUTED_SOURCE_BUILDERS` — wire-type → `ComputedDataSource`
 * constructor registry. `parseSource` (in `./registry.ts`)
 * consults this after checking for `"formula"`, so any wire
 * type here lands its row as a `ComputedDataSource` subclass
 * with engine-managed cells.
 *
 * Register each new computed row type once at module load via
 * the side effect at the bottom — consumers that import
 * `@tento/lona/sheets` get the built-ins by default.
 */

import type { Option } from "../../core/option.ts";
import { StaticRegistry } from "../../sheet/static-registry.ts";
import type { SourceDescriptor } from "../descriptor.ts";
import type { RowLocalId } from "../../ids/row-local-id.ts";
import type { ModelSheet } from "../../sheet/rows-sheet.ts";

import { CalendarSystemRow } from "./calendar-system-row.ts";
import type { ComputedDataSource } from "./computed-data-source.ts";
import { DerivedDataSource } from "./derived-data-source.ts";
import { ListDataSource } from "./list-data-source.ts";

/** Builds the source row a computed source descriptor names (a derived or list data source) for a sheet. */
export type ComputedSourceBuilder = (
  id: RowLocalId,
  descriptor: SourceDescriptor,
  sheet: ModelSheet,
) => ComputedDataSource;

/**
 * Package-internal: populated with the built-ins at module load and frozen.
 * A test layers its fixture builders on the client it builds
 * (`SheetRegistries.extend`), never here.
 */
export const computedSourceRegistry = new StaticRegistry<ComputedSourceBuilder>(
  "COMPUTED_SOURCE_BUILDERS",
);

/** Wire type → computed-source builder, read only. Frozen after bootstrap. */
export const COMPUTED_SOURCE_BUILDERS = {
  /** Look up a builder by wire type. `null` when unregistered. */
  get(wireType: string): Option<ComputedSourceBuilder> {
    return computedSourceRegistry.get(wireType);
  },
} as const;

// ── Built-in registration ────────────────────────────────────────

/**
 * Register the built-in computed-source builders, then freeze. Called
 * automatically as a module side effect (package-internal; not on the
 * barrel).
 */
function registerBuiltinComputedSources(): void {
  computedSourceRegistry.register(
    "calendar-system",
    (id, d, sheet) => new CalendarSystemRow(id, d, sheet),
  );
  computedSourceRegistry.register(
    "list",
    (id, d, sheet) => new ListDataSource(id, d, sheet),
  );
  computedSourceRegistry.register(
    "derived",
    (id, d, sheet) => new DerivedDataSource(id, d, sheet),
  );
  computedSourceRegistry.freeze();
}

registerBuiltinComputedSources();
